// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { isNil } from 'lodash'
import { defineStore } from 'pinia'
import { BREADCRUMBS_DEFAULTS_URL, BREADCRUMBS_URL } from '../helpers/apiURLS'
import getApiData from '../helpers/getApiData'

type BreadcrumbsData = {
    companies: { id: number, name: string, visible: boolean }[]
    portfolios: { id: number, name: string, default_reporting_year: number }[]
    assets: { id: number, name: string }[]
    reportingYears: number[]
    pendingYears: number[]
    companyId: number | null
    portfolioId: number | null
    assetId: number | null
    reportingYear: number | null
    isLoadingOptions: boolean
}

type Selected = {
    companyId: number | null
    portfolioId: number | null
    assetId: number | null
    reportingYear: number | null
}

type PrepareFiltersType = {
    include_portfolios_visible_through_assets?: 'true' | 'false'
    company_id?: number | string
    portfolio_id?: number | string
    asset_id?: number | string
    show_incomplete_assets?: 'true' | 'false'
    reporting_year?: number | string
    company_wide?: 'true' | 'false'
}

/**
 * @deprecated Use `useBreadcrumb_experimental()` from `@/vue/components/navigation/BreadcrumbNav/use-breadcrumbs`
 * and `useCompanyList()` / `useCompanyHierarchy()` for data access instead.
 */
export const useBreadcrumbsStore = defineStore('breadcrumbsStore', {
    state: (): BreadcrumbsData => {
        return {
            companies: [],
            portfolios: [],
            assets: [],
            reportingYears: [],
            companyId: null,
            portfolioId: null,
            assetId: null,
            reportingYear: null,
            pendingYears: [],
            isLoadingOptions: false,
        }
    },
    getters: {
        options(): any {
            return {
                companies: this.companies,
                portfolios: this.portfolios,
                assets: this.assets,
                reportingYears: this.reportingYears,
            }
        },
        selected(): Selected {
            return {
                companyId: this.companyId,
                portfolioId: this.portfolioId,
                assetId: this.assetId,
                reportingYear: this.reportingYear,
            }
        },
        isPendingYear(): boolean {
            return !isNil(this.reportingYear) && (this.pendingYears?.includes(Number(this.reportingYear)) ?? false)
        },
        lastReportingYear(): number {
            if (this.reportingYears && this.reportingYears.length > 0) {
                return this.reportingYears.sort((a, b) => b - a)?.[0]
            }
            return new Date().getFullYear()
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        currentAsset(): any {
            if (this.assetId && this.assets) {
                return this.assets.find(elem => elem.id == this.assetId)
            }
            return null
        },
    },
    actions: {
        toNumberOrNull(value: unknown): number | null {
            if (value === null || value === undefined) {
                return null
            }
            const num = Number(value)
            return isNaN(num) ? null : num
        },
        setCompanyId(id: number | null) {
            this.companyId = this.toNumberOrNull(id)
        },
        setPortfolioId(id: number | null) {
            this.portfolioId = this.toNumberOrNull(id)
        },
        setAssetId(id: number | null) {
            this.assetId = this.toNumberOrNull(id)
        },
        setYear(reportingYear: number | null) {
            this.reportingYear = reportingYear?.toString() ?? (new Date().getFullYear() - 1).toString()
        },
        async loadOptions(includePortfoliosVisibleThroughAssets: boolean = false, companyWide: boolean = false) {
            const response = await getApiData(BREADCRUMBS_URL, this.prepareFilters(includePortfoliosVisibleThroughAssets, companyWide))
            this.companies = response.data.data.companies
            if (!this.companyId && response.data.data.portfolio_company) {
                this.companyId = response.data.data.portfolio_company
            }
            this.portfolios = response.data.data.portfolios
            this.assets = response.data.data.assets
            this.reportingYears = response.data.data.reporting_years
            this.pendingYears = response.data.data.pending_years
        },
        async initializeDefaultData() {
            return
            const response = await getApiData(BREADCRUMBS_DEFAULTS_URL)
            this.setCompanyId(response.data.data.company_id)
            this.setPortfolioId(response.data.data.portfolio_id)
            this.setAssetId(response.data.data.asset_id)
            this.setYear(response.data.data.default_reporting_year)
        },
        setSelectedOptionsFromCurrentRoute() {
            const currentRoute = this.$router.currentRoute.value

            if (Number(currentRoute.params.company_id)) {
                this.setCompanyId(this.toNumberOrNull(currentRoute.params.company_id))
            }
            if (Number(currentRoute.params.portfolio_id)) {
                this.setPortfolioId(this.toNumberOrNull(currentRoute.params.portfolio_id))
            }
            if (Number(currentRoute.params.asset_id)) {
                this.setAssetId(this.toNumberOrNull(currentRoute.params.asset_id))
            }
            // clear asset id in case when user navigates from asset page to portfolio/company page to prevent passing it as filter in api calls
            if (!currentRoute.params.asset_id && this.assetId) {
                this.setAssetId(null)
            }
            if (Number(currentRoute.params.reporting_year)) {
                this.setYear(this.toNumberOrNull(currentRoute.params.reporting_year))
            }
        },
        prepareFilters(includePortfoliosVisibleThroughAssets: boolean = false, companyWide: boolean = false): PrepareFiltersType {
            const filters: PrepareFiltersType = {}
            if (includePortfoliosVisibleThroughAssets) {
                filters.include_portfolios_visible_through_assets = includePortfoliosVisibleThroughAssets.toString()
            }
            if (this.companyId) {
                filters.company_id = this.companyId
            }
            if (this.portfolioId) {
                filters.portfolio_id = this.portfolioId
            }
            if (this.assetId && this.assetId !== 'new') {
                filters.asset_id = this.assetId
            }
            if (this.$router.currentRoute.value.meta.showIncompleteAssetsInBreadcrumbs) {
                filters.show_incomplete_assets = 'true'
            }
            if (this.reportingYear) {
                filters.reporting_year = this.reportingYear
            }
            if (companyWide) {
                filters.company_wide = 'true'
                delete filters.portfolio_id
            }
            return filters
        },
        setIsLoadingOptions(isLoading: boolean) {
            this.isLoadingOptions = isLoading
        },
    },
})
